export type PoiAreaType = 'circle' | 'polygon';

/** GeoJSON Polygon: { type: 'Polygon', coordinates: [[[lng,lat], [lng,lat], ...]] }
 *  Il primo "ring" e' l'outer boundary; usiamo solo quello. La ring deve essere
 *  chiusa, ovvero il primo e l'ultimo punto coincidono. */
export interface GeoJsonPolygon {
  type: 'Polygon';
  coordinates: number[][][];
}

/** Colore di default per area/bordo dei POI (cerchio standard del primo design). */
export const POI_DEFAULT_COLOR = '#2e97f2';

export class Poi {

  _id: string;
  idOrganization: string;
  name: string;
  images: string;
  position: any;            // GeoJSON Point — per il cerchio = centro, per il poligono = centroide
  radius: number;           // Solo per area_type='circle'
  area_type: PoiAreaType;   // 'circle' (default, retrocompat) | 'polygon'
  area: GeoJsonPolygon;     // Solo per area_type='polygon': il poligono GeoJSON completo
  color: string;            // Colore area/bordo (hex, es. "#2e97f2"). Default POI_DEFAULT_COLOR
  textColor: string;        // Colore testo leggibile su `color` (#fff o #374957), calcolato in setData
  timestampLastUpdate: number;
  timestampCreate: number;
  address: string;
  label_number: number;

  // Addon Consegne: campi opzionali del P.O.I.
  codice_cliente: string;
  note: string;
  giorni_consegna: number[];   // indici 0..6 (Lun..Dom)
  orari_consegna: string[][];  // [["08:00","12:00"], ...]

  // Aggiunti frontend
  latitude: number;
  longitude: number;
  hideOnMap: boolean;
  hideInList: boolean;
  hideInListTimeout: any;
  image: string;

  constructor(data?: any) {
    if(data){
      this.setData(data);
    }
  }

  setData(data: any){
    this._id = data._id;
    this.name = data.name;
    this.idOrganization = data.idOrganization;
    this.images = data.images;
    this.position = data.position;
    this.radius = data.radius;
    /* Backward compat: POI vecchi non hanno area_type → li trattiamo come 'circle'. */
    this.area_type = (data.area_type === 'polygon') ? 'polygon' : 'circle';
    this.area = data.area;
    /* Colore area: se non settato lato server usiamo il default blu storico. */
    this.color = data.color || POI_DEFAULT_COLOR;
    /* Colore testo leggibile su `color` (WCAG luminanza relativa). */
    this.textColor = Poi.pickContrastText(this.color);
    this.timestampLastUpdate = data.timestampLastUpdate;
    this.timestampCreate = data.timestampCreate;
    this.address = data.address || data.adress; // cambiare poi con address
    this.label_number = data.label_number;
    this.image = data.image;
    this.codice_cliente = data.codice_cliente || '';
    this.note = data.note || '';
    this.giorni_consegna = Array.isArray(data.giorni_consegna) ? data.giorni_consegna : [];
    this.orari_consegna = Array.isArray(data.orari_consegna) ? data.orari_consegna : [];
    if(this.position && this.position.coordinates && this.position.coordinates.length === 2){
      this.latitude = this.position.coordinates[1];
      this.longitude = this.position.coordinates[0];
    }
  }

  /** True se questo POI ha un'area poligonale (Modello A1). */
  isPolygon(): boolean {
    return this.area_type === 'polygon' && !!this.area
      && Array.isArray(this.area.coordinates)
      && Array.isArray(this.area.coordinates[0])
      && this.area.coordinates[0].length >= 4; // 3 vertici + ring closure
  }

  /** Restituisce i vertici del poligono come array di {lat,lng} (senza ring closure
   *  duplicata). [] se non e' un POI poligonale. */
  getPolygonVertices(): { lat: number; lng: number }[] {
    if(!this.isPolygon()) return [];
    const ring = this.area.coordinates[0];
    // L'ultimo punto coincide col primo (ring closure GeoJSON): lo escludo.
    const verts = ring.slice(0, ring.length - 1);
    return verts.map(([lng, lat]: number[]) => ({ lat, lng }));
  }

  /** True se il punto {lat,lng} cade dentro l'area del POI (poligono o cerchio). */
  containsPoint(point: { lat: number; lng: number }): boolean {
    if(!point || point.lat == null || point.lng == null) return false;
    if(this.isPolygon()){
      return Poi.isPointInPolygon(point, this.getPolygonVertices());
    }
    // Cerchio: distanza haversine dal centro <= raggio (m).
    if(this.latitude == null || this.longitude == null || !this.radius) return false;
    return Poi.haversineMeters(point, { lat: this.latitude, lng: this.longitude }) <= this.radius;
  }

  /** Ray casting su lng/lat (equivalente a poi-map.service.ts / utils.js isInPoi). */
  private static isPointInPolygon(point: { lat: number; lng: number }, pts: { lat: number; lng: number }[]): boolean {
    if(!point || !pts || pts.length < 3) return false;
    let inside = false;
    const x = point.lng, y = point.lat;
    for(let i = 0, j = pts.length - 1; i < pts.length; j = i++){
      const xi = pts[i].lng, yi = pts[i].lat;
      const xj = pts[j].lng, yj = pts[j].lat;
      const intersect = ((yi > y) !== (yj > y))
        && (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
      if(intersect) inside = !inside;
    }
    return inside;
  }

  /** Restituisce '#ffffff' o '#374957' a seconda del contrasto sul colore di sfondo.
   *  Usa la luminanza relativa WCAG: se L > 0.5 -> testo scuro, altrimenti bianco. */
  private static pickContrastText(hex: string): string {
    if(!hex) return '#374957';
    const m = hex.replace('#', '');
    const h = m.length === 3 ? m.split('').map(c => c + c).join('') : m;
    if(h.length !== 6) return '#374957';
    const lin = (c: number) => { const v = c / 255; return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); };
    const L = 0.2126 * lin(parseInt(h.slice(0, 2), 16))
            + 0.7152 * lin(parseInt(h.slice(2, 4), 16))
            + 0.0722 * lin(parseInt(h.slice(4, 6), 16));
    return L > 0.5 ? '#374957' : '#ffffff';
  }

  /** Distanza in metri tra due punti (formula di Haversine). */
  private static haversineMeters(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
    const R = 6371000;
    const toRad = (d: number) => d * Math.PI / 180;
    const phi1 = toRad(a.lat), phi2 = toRad(b.lat);
    const dPhi = toRad(b.lat - a.lat), dLam = toRad(b.lng - a.lng);
    const s = Math.sin(dPhi / 2) ** 2 + Math.cos(phi1) * Math.cos(phi2) * Math.sin(dLam / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
  }
}

/**
 * P.O.I. mostrati nella demo pubblica. Sono dati locali: vengono caricati in
 * DataService.pois da initializeDemoOrganization() senza passare dall'API,
 * allo stesso modo di DEMO_ROUTES per i percorsi dei veicoli.
 * Le coordinate sono su Milano, dove si muovono i veicoli demo.
 */
export const DEMO_POIS: any[] = [
  {
    _id: '6aa153548b61e1e4185b663e',
    name: 'Citylife',
    label_number: 2,
    position: {
      type: 'Point',
      coordinates: [9.155441494091654, 45.47749594661815]
    },
    area_type: 'polygon',
    area: {
      type: 'Polygon',
      coordinates: [[
        [9.151539912892272, 45.48058465986418],
        [9.151303878498961, 45.47434061131763],
        [9.152140727711608, 45.47372369185772],
        [9.155638328267028, 45.47354312878703],
        [9.158883382079889, 45.47343591913983],
        [9.159607997609069, 45.47369359805276],
        [9.159951320362975, 45.47725960206182],
        [9.159886947346617, 45.48007312528147],
        [9.156754127217223, 45.48070502026752],
        [9.153621307087828, 45.48269092980147],
        [9.151046386433531, 45.48097583023498],
        [9.151539912892272, 45.48058465986418]
      ]]
    },
    color: '#2e97f2',
    idOrganization: 'demo',
    timestampCreate: 1788957524,
    timestampLastUpdate: 1788957525,
    address: 'Piazza Tre Torri, 1, 20145, Milano, Milano, Italia',
    image: './assets/img/markers-demo/poi-citylife.jpeg',
    radius: null,
    codice_cliente: '',
    giorni_consegna: [],
    note: '',
    orari_consegna: []
  }
];
